import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { teamAppsQueryCreator } from '@/query/teamAppsQueryCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { userWorkspaceQueryCreator } from '@/query/userWorkspaceQueryCreator';
import { workspaceQueryCreator } from '@/query/workspaceQueryCreator';
import { BuiltInApps } from '@/shared/types/apps';
import { ValidZoomServiceType } from '@/shared/types/auth';
import { filterToRejectedReasons } from '@/shared/utils/promise';
import { getConnectedAppsQueryCreator } from '../../query/getConnectedAppsQueryCreator';
import type { ZoomSyncedAccount } from '../../type/apps';
import type { ZoomConnectedApp } from '../../type/ConnectedApps';
import { useConnectedApps } from '../useConnectedApps';
import { useSyncedAccounts } from '../useSyncedAccounts';

export type UpdateZoomSettingsVars = {
  /**
   * Optional list of Zoom accounts to update settings for. If not provided, all connected accounts will be updated.
   */
  accountsToUpdate?: ZoomSyncedAccount[];
  /**
   * The Zoom service type to update (live_notes or cloud_sync)
   */
  zoomServiceType: ValidZoomServiceType;
  /**
   * Whether to enable or disable the service
   */
  enabled: boolean;
};

export type DisableAllZoomSettingsVars = {
  /**
   * Optional list of Zoom accounts to disable settings for. If not provided, all connected accounts will be disabled.
   */
  accountsToDisable?: ZoomSyncedAccount[];
};

export type ZoomAccountServiceSettings = {
  account: ZoomSyncedAccount;
  enabled: Record<ValidZoomServiceType, boolean | null>;
  has: Record<ValidZoomServiceType, boolean | null>;
  issues: Record<ValidZoomServiceType, string[] | null>;
};

export type ZoomSettings = {
  /**
   * Update the settings for a specific Zoom accounts
   */
  updateZoomSettings: (vars: UpdateZoomSettingsVars) => Promise<void>;
  /**
   * Disable all Zoom settings for a specific accounts
   */
  disableAllZoomSettings: (vars: DisableAllZoomSettingsVars) => Promise<void>;
  /**
   * Whether any mutation is in progress
   */
  isPending: boolean;
  /**
   * Whether all the mutations were successful
   */
  isSuccess: boolean;
  /**
   * Whether any of the mutations resulted in an error
   */
  isError: boolean;
  /**
   * Whether the initial data is still loading
   */
  isLoading: boolean;
  /**
   * The current settings for each connected Zoom account
   * @example
   * ```tsx
   * const { serviceSettings } = useZoomSettings();
   *
   * return <div>
   *   {serviceSettings.map((setting) => (
   *     <div key={setting.account.id}>
   *       Account: {setting.account.name}<br/>
   *       Cloud Sync: {setting.enabled.cloud_sync ? 'Enabled' : 'Disabled'}<br/>
   *       Live Notes: {setting.enabled.live_notes ? 'Enabled' : 'Disabled'}
   *     </div>
   *   ))}
   * </div>
   * ```
   */
  serviceSettings: ZoomAccountServiceSettings[];
};

/**
 * Hook to read, update and disable Zoom settings for connected accounts
 * @example
 * ```tsx
 * const { serviceSettings, updateZoomSettings, disableAllZoomSettings } = useZoomSettings();
 *
 * return (
 *   <div>
 *     <div>
 *       {serviceSettings.map((setting) => (
 *         <div key={setting.account.id}>
 *           Account: {setting.account.name}<br/>
 *           <p>
 *             Cloud Sync: {setting.enabled.cloud_sync ? 'Enabled' : 'Disabled'}
 *             <button
 *               onClick={() => updateZoomSettings({
 *                 accountsToUpdate: [setting.account],
 *                 zoomServiceType: ValidZoomServiceTypes.CLOUD_SYNC,
 *                 enabled: !setting.enabled.cloud_sync,
 *               })}
 *             >
 *               Toggle Cloud Sync
 *             </button>
 *           </p>
 *           <p>
 *             Live Notes: {setting.enabled.live_notes ? 'Enabled' : 'Disabled'}
 *             <button
 *               onClick={() => updateZoomSettings({
 *                 accountsToUpdate: [setting.account],
 *                 zoomServiceType: ValidZoomServiceTypes.LIVE_NOTES,
 *                 enabled: !setting.enabled.live_notes,
 *               })}
 *             >
 *               Toggle Live Notes
 *             </button>
 *           </p>
 *         </div>
 *       ))}
 *     </div>
 *     <button
 *       onClick={() => disableAllZoomSettings({
 *         accountsToDisable: serviceSettings.map((setting) => setting.account),
 *       })}
 *     >
 *       Disable All
 *     </button>
 *   </div>
 * );
 * ```
 */
export function useZoomSettings(): ZoomSettings {
  const queryClient = useQueryClient();
  const {
    connectedApps,
    isLoading: isLoadingConnectedApps,
    isPending: isPendingConnectedApps,
  } = useConnectedApps({
    service: BuiltInApps.ZOOM,
  });
  const { accounts, isLoading: isLoadingAccounts } = useSyncedAccounts({
    service: BuiltInApps.ZOOM,
  });

  // === updateZoomSettings ===
  const {
    mutateAsync: updateZoomSettings,
    isPending: isUpdateZoomSettingsPending,
    isSuccess: isUpdateZoomSettingsSuccess,
    isError: isUpdateZoomSettingsError,
  } = useMutation({
    mutationFn: async ({
      accountsToUpdate,
      zoomServiceType,
      enabled,
    }: UpdateZoomSettingsVars) => {
      const accs = accountsToUpdate ?? (accounts as ZoomSyncedAccount[]);
      const results = await Promise.allSettled(
        accs.map((account) =>
          updateSettings({
            zoomCredentialId: account.zoom_credential_id.toString(),
            zoomServiceType,
            enabled,
          }),
        ),
      );

      const errors = filterToRejectedReasons(results);

      if (errors.length > 0) {
        throw new UpdateZoomSettingsError(
          accs,
          zoomServiceType,
          enabled,
          errors,
        );
      }
    },
    onSettled: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(userWorkspaceQueryCreator()),
        queryClient.invalidateQueries(workspaceQueryCreator()),
      ]),
  });

  // === disableAllZoomSettings ===
  // TODO(sleahy): Call this when the user disconnects their Zoom account
  const {
    mutateAsync: disableAllZoomSettings,
    isPending: isDisableAllZoomSettingsPending,
    isSuccess: isDisableAllZoomSettingsSuccess,
    isError: isDisableAllZoomSettingsError,
  } = useMutation({
    mutationFn: async ({ accountsToDisable }: DisableAllZoomSettingsVars) => {
      const accs = accountsToDisable ?? (accounts as ZoomSyncedAccount[]);
      const results = await Promise.allSettled(
        accs.map((account) =>
          disableAllSettings(
            (account as ZoomSyncedAccount).zoom_credential_id.toString(),
          ),
        ),
      );

      const errors = filterToRejectedReasons(results);
      if (errors.length > 0) {
        throw new DisableAllZoomSettingsError(accs, errors);
      }
    },
    onSettled: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(userWorkspaceQueryCreator()),
        queryClient.invalidateQueries(workspaceQueryCreator()),
      ]),
  });

  // === serviceSettings ===
  const serviceSettings = useMemo(() => {
    try {
      return connectedApps
        .map<ZoomAccountServiceSettings | null>((app) => {
          const account = accounts?.find(
            (account) => account.account === app.account,
          );
          if (!account) {
            return null;
          }
          return {
            account,
            enabled: {
              [ValidZoomServiceType.CLOUD_SYNC]: Boolean(
                app.cloud_sync_enabled,
              ),
              [ValidZoomServiceType.LIVE_NOTES]: Boolean(
                app.live_notes_enabled,
              ),
            },
            has: {
              [ValidZoomServiceType.CLOUD_SYNC]: Boolean(
                app.cloud_sync_enabled,
              ),
              [ValidZoomServiceType.LIVE_NOTES]: Boolean(
                app.live_notes_enabled,
              ),
            },
            issues: {
              [ValidZoomServiceType.CLOUD_SYNC]: null,
              [ValidZoomServiceType.LIVE_NOTES]:
                app.live_notes_setting_issues ?? null,
            },
          };
        })
        .filter(
          (setting): setting is ZoomAccountServiceSettings => setting !== null,
        );
    } catch (error) {
      throw new FailedToGetZoomSettingsError(error, connectedApps, accounts);
    }
  }, [connectedApps, accounts]);

  // === isPending, isSuccess, isError, isLoading ===
  const isPending =
    isUpdateZoomSettingsPending ||
    isDisableAllZoomSettingsPending ||
    isPendingConnectedApps;
  const isSuccess =
    isUpdateZoomSettingsSuccess || isDisableAllZoomSettingsSuccess;
  const isError = isUpdateZoomSettingsError || isDisableAllZoomSettingsError;
  const isLoading = isLoadingAccounts || isLoadingConnectedApps;

  return {
    updateZoomSettings,
    disableAllZoomSettings,
    isPending,
    isSuccess,
    isError,
    isLoading,
    serviceSettings,
  };
}

async function updateSettings({
  zoomCredentialId,
  zoomServiceType,
  enabled,
}: {
  zoomCredentialId: string;
  zoomServiceType: ValidZoomServiceType;
  enabled: boolean;
}) {
  const body = new FormData();
  body.append('zoom_credential_id', zoomCredentialId);
  body.append('feature', zoomServiceType);
  body.append('enabled', enabled.toString());

  await legacyRestFetch('update_zoom_settings', {
    method: 'POST',
    body,
  });
}

async function disableAllSettings(zoomCredentialId: string) {
  await updateSettings({
    zoomCredentialId,
    zoomServiceType: ValidZoomServiceType.CLOUD_SYNC,
    enabled: false,
  });
  await updateSettings({
    zoomCredentialId,
    zoomServiceType: ValidZoomServiceType.LIVE_NOTES,
    enabled: false,
  });
}

class UpdateZoomSettingsError extends Error {
  name = 'UpdateZoomSettingsError';
  constructor(
    public readonly accountsToUpdate: ZoomSyncedAccount[],
    public readonly zoomServiceType: ValidZoomServiceType,
    public readonly enabled: boolean,
    public readonly reasons: unknown[],
  ) {
    const accountNames = accountsToUpdate.map((a) => a.account).join(', ');
    super(
      `Failed to update Zoom settings ${zoomServiceType} for accounts ${accountNames}`,
    );
  }
}

class DisableAllZoomSettingsError extends Error {
  name = 'DisableAllZoomSettingsError';
  constructor(
    public readonly accountsToUpdate: ZoomSyncedAccount[],
    public readonly reasons: unknown[],
  ) {
    const accountNames = accountsToUpdate.map((a) => a.account).join(', ');
    super(`Failed to disable all Zoom settings for accounts ${accountNames}`);
  }
}

class FailedToGetZoomSettingsError extends Error {
  name = 'FailedToGetZoomSettingsError';
  constructor(
    public readonly cause: unknown,
    public readonly connectedApps: ZoomConnectedApp[],
    public readonly accounts: ZoomSyncedAccount[],
  ) {
    super('Failed to get Zoom settings');
  }
}
